import type { Translations } from "@/src/i18n/translations";
import type {
  ReportReason,
  ReportStatus,
  ReportTarget,
} from "@/src/pages/Reports/reports.type";

type ReportsKey = keyof Translations["reports"];

/**
 * Énumérations du serveur → clés du dictionnaire.
 *
 * Des tables explicites, et **jamais** un gabarit `` `reason-${reason}` `` :
 *
 * - le serveur parle `snake_case` (`inappropriate_content`), le dictionnaire
 *   `kebab-case` — un gabarit imposerait sa casse à l'un des deux ;
 * - `satisfies Record<…>` fait échouer `tsc` sur les **deux** bords : une
 *   valeur ajoutée au contrat sans libellé, et un libellé qui ne pointe aucune
 *   clé existante. Un gabarit ne vérifie ni l'un ni l'autre, et produit un
 *   `undefined` à l'écran le jour où le serveur ajoute un motif.
 */
export const STATUS_KEY = {
  pending: "status-pending",
  actioned: "status-actioned",
  dismissed: "status-dismissed",
} as const satisfies Record<ReportStatus, ReportsKey>;

export const TARGET_KEY = {
  message: "target-message",
  account: "target-account",
  event: "target-event",
  post: "target-post",
  comment: "target-comment",
} as const satisfies Record<ReportTarget, ReportsKey>;

export const REASON_KEY = {
  spam: "reason-spam",
  harassment: "reason-harassment",
  inappropriate_content: "reason-inappropriate-content",
  impersonation: "reason-impersonation",
  child_safety: "reason-child-safety",
  other: "reason-other",
} as const satisfies Record<ReportReason, ReportsKey>;

/**
 * Gravité du motif, portée par la couleur de la puce.
 *
 * Le rouge est réservé à ce qui vise une **personne** — harcèlement, contenu
 * inapproprié : c'est ce qui doit sortir du lot quand on parcourt une file de
 * trente cartes. Le spam gêne, il ne met personne en danger.
 *
 * ⚠️ **`child_safety` est rouge, et il ne doit jamais cesser de l'être.** Les
 * *Child Safety Standards* de Google engagent l'éditeur à agir **dès la
 * connaissance effective** : un signalement CSAE qui se fond dans la file est
 * exactement ce que cette obligation interdit. C'est aussi la raison d'être du
 * motif — avant lui, le cas arrivait noyé dans « contenu inapproprié ».
 */
export const REASON_COLOR = {
  spam: "warning",
  harassment: "danger",
  inappropriate_content: "danger",
  impersonation: "warning",
  child_safety: "danger",
  other: "default",
} as const satisfies Record<ReportReason, "danger" | "default" | "warning">;
